import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, firstValueFrom } from "rxjs";
import { AppConsts } from "@shared/app-consts";
import {
  AddInvoiceItemRequest,
  AddInvoiceRequest,
  AddInvoiceResponse,
  CloneInvoiceResponse,
  DeleteInvoiceResponse,
  EditInvoiceItemRequest,
  EditInvoiceRequest,
  EditInvoiceResponse,
  EInvoiceAssuranceType,
  EInvoiceDto,
  EInvoiceItemDto,
  EInvoiceItemType,
  EInvoiceListModel,
  EInvoiceType,
  InvoiceStatus,
  ListInvoiceByEmailRequest,
  ListInvoiceRequest,
  PaymentMethodType,
  PublishInvoiceResponse,
} from "@shared/e-invoice";
import { BaseService } from "@services/base.service";
import { LabelValueModel } from "@shared/models/common/label-value.model";
import { EnumService } from "@services/common";

@Injectable({
  providedIn: "root",
})
export class EInvoiceService extends BaseService {
  constructor(private http: HttpClient) {
    super();
  }
  list(request: ListInvoiceRequest): Observable<EInvoiceListModel> {
    let httpOptions = this.getHttpHeader(false, this.cleanEmpty(request));

    return this.http.get(
      AppConsts.API_SERVICE_URL + "ElectronicInvoice",
      httpOptions
    ) as Observable<EInvoiceListModel>;
  }

  item(id: number): Observable<EInvoiceDto> {
    let httpOptions = this.getHttpHeader(false);

    return this.http.get(
      AppConsts.API_SERVICE_URL + `ElectronicInvoice/${id}`,
      httpOptions
    ) as Observable<EInvoiceDto>;
  }

  new(companyId: number): Observable<EInvoiceDto> {
    let httpOptions = this.getHttpHeader(false);

    return this.http.get(
      AppConsts.API_SERVICE_URL + `ElectronicInvoice/${companyId}/New`,
      httpOptions
    ) as Observable<EInvoiceDto>;
  }

  edit(
    id: number,
    request: EditInvoiceRequest
  ): Observable<EditInvoiceResponse> {
    let httpOptions = this.getHttpHeader(false);

    return this.http.put(
      AppConsts.API_SERVICE_URL + `ElectronicInvoice/${id}`,
      request,
      httpOptions
    ) as Observable<EditInvoiceResponse>;
  }

  add(request: AddInvoiceRequest): Observable<AddInvoiceResponse> {
    let httpOptions = this.getHttpHeader(false);

    return this.http.post(
      AppConsts.API_SERVICE_URL + `ElectronicInvoice`,
      request,
      httpOptions
    ) as Observable<AddInvoiceResponse>;
  }
  getAllInvoicesPerUser(request : string): Observable<EInvoiceListModel> {
  let httpOptions = this.getHttpHeader(false);
     return this.http.post<EInvoiceListModel>(AppConsts.API_SERVICE_URL + "ElectronicInvoice/ListGeneratedInvoiceByUserAsync", JSON.stringify(request) ,  httpOptions);
     
}
  delete(id: number): Observable<DeleteInvoiceResponse> {
    let httpOptions = this.getHttpHeader(false);

    return this.http.delete(
      AppConsts.API_SERVICE_URL + `ElectronicInvoice/${id}`,
      httpOptions
    ) as Observable<DeleteInvoiceResponse>;
  }

  // publish/clone : httpOptions était passé comme 2e argument de post() — donc envoyé comme CORPS
  // de la requête ({"headers":…,"params":{}}) et jamais appliqué comme options. L'action serveur
  // ne lit pas de corps (l'anomalie était latente, héritée de V1) ; corps vide explicite + options
  // à leur place, comme tous les autres appels du module.
  publish(companyId: number, id: number): Observable<PublishInvoiceResponse> {
    let httpOptions = this.getHttpHeader(false);

    return this.http.post(
      AppConsts.API_SERVICE_URL + `ElectronicInvoice/${companyId}/${id}/publish`,
      null,
      httpOptions
    ) as Observable<PublishInvoiceResponse>;
  }

  clone(id: number): Observable<CloneInvoiceResponse> {
    let httpOptions = this.getHttpHeader(false);

    return this.http.post(
      AppConsts.API_SERVICE_URL + `ElectronicInvoice/${id}/clone`,
      null,
      httpOptions
    ) as Observable<CloneInvoiceResponse>;
  }

DownloadDirigeantInvoice(id: number) {
    let httpOptions = this.getHttpHeader(true);

    return firstValueFrom(this.http
      .get(
        AppConsts.API_SERVICE_URL + `ElectronicInvoice/${id}/DownloadDirigeantInvoice`,
        httpOptions
      ))
      .then((response: any) => {
        return this.getFileFromResponse(response);
      });
  }


  download(id: number) {
    let httpOptions = this.getHttpHeader(true);

    return firstValueFrom(this.http
      .get(
        AppConsts.API_SERVICE_URL + `ElectronicInvoice/${id}/download`,
        httpOptions
      ))
      .then((response: any) => {
        return this.getFileFromResponse(response);
      });
  }

  checkInvoiceCoherence(invoice: EInvoiceDto): boolean {
    if (
      (invoice.Preference.PaymentMethod == PaymentMethodType.PayoutSepa ||
        invoice.Preference.PaymentMethod == PaymentMethodType.Payout) &&
      invoice.BankAccount == null
    )
      return false;
    if (invoice.Client == null) return false;
    return true;
  }

  getStatusLabel(status: InvoiceStatus): string {
    switch (status) {
      case InvoiceStatus.Pending:
        return "Brouillon";
      case InvoiceStatus.Published:
        return "Validée ";
      default:
        return "";
    }
  }

  getStatusColor(status: InvoiceStatus): string {
    switch (status) {
      case InvoiceStatus.Pending:
        return "rgb(3, 194, 252)";
      case InvoiceStatus.Published:
        return "#1cc88a";
    }
  }

  getStatusOptions(): LabelValueModel[] {
    return EnumService.getNames(InvoiceStatus).map((key) => {
      const item = InvoiceStatus[key];
      const itemLabel = this.getStatusLabel(item);

      return new LabelValueModel(itemLabel, key);
    });
  }

  getInvoiceTypeLabel(status: EInvoiceType): string {
    switch (status) {
      case EInvoiceType.Invoice:
        return "Facture";
      case EInvoiceType.CreditNote:
        return "Avoir";
      case EInvoiceType.PrepaymentInvoice:
        return "Facture acompte";
      case EInvoiceType.Quote:
        return "Devis";
      case EInvoiceType.PurchaseOrder:
        return "Bon de commande";
        case EInvoiceType.DeliveryOrder:
          return "Bon de livraison";
      default:
        return "Facture";
    }
  }

  getInvoiceItemTypeLabel(status: EInvoiceItemType): string {
    switch (status) {
      case EInvoiceItemType.Product:
        return "Produit";
      case EInvoiceItemType.Service:
        return "Service";
      case EInvoiceItemType.PrepaymentInvoice:
        return "Acompte";
      default:
        return "Autre";
    }
  }

  getInvoiceTypeOptions(): LabelValueModel[] {
    return EnumService.getNames(EInvoiceType).map((key) => {
      const item = EInvoiceType[key];
      const itemLabel = this.getInvoiceTypeLabel(item);
      return new LabelValueModel(itemLabel, { key, item });
      
    });
  }

  getInvoiceItemTypeOptions(): LabelValueModel[] {
    return EnumService.getNames(EInvoiceItemType).map((key) => {
      const item = EInvoiceItemType[key];
      const itemLabel = this.getInvoiceItemTypeLabel(item);

      return new LabelValueModel(itemLabel, { key, item });
    });
  }

  mapAddInvoiceItemRequestCollection(
    models: EInvoiceItemDto[]
  ): AddInvoiceItemRequest[] {
    return models.map((x) => this.mapAddInvoiceItemRequest(x));
  }

  mapEditInvoiceItemRequestCollection(
    models: EInvoiceItemDto[]
  ): EditInvoiceItemRequest[] {
    return models.map((x) => this.mapEditInvoiceItemRequest(x));
  }

  mapAddInvoiceItemRequest(model: EInvoiceItemDto): AddInvoiceItemRequest {
    return {
      Title: model.Title,
      Description: model.Description,
      Quantity: model.Quantity,
      TvaType: model.TvaType,
      UnitPrice: model.UnitPrice,
      VatPercent: model.VatPercent,
      Type: model.Type,
      DiscountPercent: model.Discount ? model.Discount.Percent : null,
      ExemptTvaZone: model.ExemptTvaZone
    };
  }

  mapEditInvoiceItemRequest(model: EInvoiceItemDto): EditInvoiceItemRequest {
    return {
      Id: model.Id,
      Title: model.Title,
      Description: model.Description,
      Quantity: model.Quantity,
      TvaType: model.TvaType,
      UnitPrice: model.UnitPrice,
      VatPercent: model.VatPercent,
      Type: model.Type,
      DiscountPercent: model.Discount ? model.Discount.Percent : null,
      ExemptTvaZone: model.ExemptTvaZone
    };
  }

  getInvoiceAssuranceTypeLabel(status: EInvoiceAssuranceType): string {
    switch (status) {
      case EInvoiceAssuranceType.Decenal:
        return "Assurance décennale";
      case EInvoiceAssuranceType.RcPro:
        return "Assurance responsabilité civile Pro";
      default:
        return "Non défini";
    }
  }

  getInvoiceAssuranceTypeOptions(): LabelValueModel[] {
    return EnumService.getNames(EInvoiceAssuranceType).map((key) => {
      const item = EInvoiceAssuranceType[key];
      const itemLabel = this.getInvoiceAssuranceTypeLabel(item);

      return new LabelValueModel(itemLabel, { key, item });
    });
  }
}
